import { Button, Modal } from 'semantic-ui-react';
import { MetadataType } from '../../types';
import { ExtractionSpecType } from '../../../../components/pdfViewer/types';
import useFormState from '../../useFormState';
import ExtractionImagePreview from '../extractionImagePreview';
import MetadataForm from '../metadataForm';
import './styles.css';
import { useEffect, useMemo } from 'react';
import { ResourceProps, toResourceProps } from '../../../../utils/reportResourceUtil';

type ExtractionModalPropsType = {
    awsUrl: string;
    open: boolean;
    onExtraction: (metadata: MetadataType) => void;
    isLoading: boolean;
    extractionSpecs: ExtractionSpecType[];
    onDelete: () => void;
    onClose: () => void;
    removeExtractionSpec: (id: string) => void;
};

function ExtractionModal({
    awsUrl,
    open,
    onExtraction,
    isLoading,
    extractionSpecs,
    onDelete,
    onClose,
    removeExtractionSpec,
}: ExtractionModalPropsType) {
    const pages = useMemo(() => {
        return Array.from(new Set(extractionSpecs.map((e) => e.page)))
            .sort((a, b) => a - b)
            .map((p) => `p${p}`)
            .join(' ');
    }, [extractionSpecs]);
    const sourceResourceProps = useMemo(() => toResourceProps(awsUrl), [awsUrl]);
    const { state, handleChange } = useFormState(getDefaultState(sourceResourceProps));
    const resetState = () => {
        handleChange(getDefaultState(sourceResourceProps));
    };

    useEffect(() => {
        resetState();
    }, [extractionSpecs]);

    const handleExtraction = () => {
        onExtraction({ ...state, suffix: `${pages} ${state.suffix}` });
        resetState();
    };

    const handleAddMore = () => {
        onClose();
        resetState();
    };

    const handleDelete = () => {
        onDelete();
        resetState();
    };

    return (
        <Modal className="extraction-modal" size="large" open={open}>
            <Modal.Header>Image Extraction</Modal.Header>
            <Modal.Content>
                <p>You are about to extract the selected area</p>
                <div>
                    {extractionSpecs.map((e) => (
                        <div key={e.id} className="extraction-modal__preview-container">
                            <ExtractionImagePreview extractionSpec={e} />
                            <Button icon="delete" negative onClick={() => removeExtractionSpec(e.id)} />
                        </div>
                    ))}
                </div>
                <MetadataForm
                    sourceType={state.sourceType}
                    finDocType={state.finDocType}
                    reportType={state.reportType}
                    note={state.note}
                    suffix={state.suffix}
                    originalOnly={state.originalOnly}
                    handleChange={handleChange}
                    pages={pages}
                />
            </Modal.Content>
            <Modal.Actions>
                <Button disabled={isLoading} loading={isLoading} positive onClick={handleExtraction}>
                    Extract
                </Button>
                <Button color="grey" onClick={handleAddMore}>
                    Add More
                </Button>
                <Button negative onClick={handleDelete}>
                    Delete
                </Button>
            </Modal.Actions>
        </Modal>
    );
}

function getDefaultState(sourceResourceProps: ResourceProps): MetadataType {
    return {
        finDocType: '',
        reportType: '',
        note: false,
        originalOnly: false,
        suffix: '',
        sourceType: sourceResourceProps.sourceType,
    };
}

export default ExtractionModal;
